import { ArrowRight } from "lucide-react";

const BRIEFING = {
  professional: {
    kicker: "Psychometric Career Interview",
    title: "Your interview room is ready.",
    subtitle:
      "You'll be asked about what you've built, who you've helped, and what makes you irreplaceable. Answer with specifics — the system compresses signal, not summaries.",
    openingQ:
      "What problem do you keep getting hired to solve, even when it's not in your job description?",
    timeNote: "Typically 8–14 questions · 20–40 minutes",
  },
  student: {
    kicker: "Early Career Interview",
    title: "Your interview room is ready.",
    subtitle:
      "You'll be asked about the projects you've built, what drew you to your field, and how you work. Specific is better than impressive — the system finds your identity in the details.",
    openingQ:
      "Tell me about the project you're most proud of — not the most impressive one, the one where you felt most like yourself doing the work.",
    timeNote: "Typically 8–14 questions · 20–35 minutes",
  },
  pivot: {
    kicker: "Career Transition Interview",
    title: "Your interview room is ready.",
    subtitle:
      "You'll be asked about both where you've been and where you're going. The goal is to find the through-line that makes your transition feel inevitable — be honest about both chapters.",
    openingQ:
      "Tell me about the move you're making — not where you've been, but where you're going, and what convinced you it was the right direction.",
    timeNote: "Typically 10–16 questions · 25–45 minutes",
  },
};

const DEFAULT = BRIEFING.professional;

export default function InterviewBriefing({ onBegin, interviewType }) {
  const b = BRIEFING[interviewType] ?? DEFAULT;

  return (
    <div
      className="briefing-shell"
      data-testid="interview-briefing"
      role="region"
      aria-label="Interview introduction"
      tabIndex={0}
    >
      <p className="briefing-kicker">{b.kicker}</p>

      <div className="briefing-title">
        <h2>{b.title}</h2>
        <p>{b.subtitle}</p>
      </div>

      <div className="briefing-stage-flow">
        <div className="briefing-step bs-active">
          <div className="bs-icon">◎</div>
          <strong>Interview</strong>
          <span>Answer questions at your own pace</span>
        </div>
        <div className="briefing-stage-arrow">→</div>
        <div className="briefing-step">
          <div className="bs-icon">⬡</div>
          <strong>Synthesis</strong>
          <span>Your answers are shaped into a coherent story</span>
        </div>
        <div className="briefing-stage-arrow">→</div>
        <div className="briefing-step">
          <div className="bs-icon">✦</div>
          <strong>Identity Spec</strong>
          <span>A clear, usable profile — ready to share</span>
        </div>
      </div>

      <div className="briefing-preview">
        <div className="briefing-preview-header">The first question you'll see</div>
        <div className="briefing-preview-stage">
          <div className="preview-blob-dot" aria-hidden="true" />
          <div className="briefing-preview-q">
            {b.openingQ}<span className="briefing-cursor" aria-hidden="true" />
          </div>
        </div>
        <div className="briefing-preview-transcript">
          <div className="bp-msg bp-system">{b.openingQ}</div>
          <div className="bp-msg bp-user">
            {interviewType === "student"
              ? "I built a real-time transit tracker for my city during my final year project. I was frustrated that existing apps were inaccurate and decided to fix it."
              : interviewType === "pivot"
              ? "I've been a product manager for eight years, and I realised I wanted to get closer to the work — specifically to the research side. I kept finding myself doing user interviews on the side."
              : "I keep getting called in when a team has been stuck on a data architecture problem for too long. It's not on my job title but it's always been the work I'm actually doing."}
          </div>
          <div className="bp-msg bp-next">
            <div className="bp-next-label">Next question appears here</div>
            {interviewType === "student"
              ? "What specifically did you build — and what broke when you first shipped it?"
              : interviewType === "pivot"
              ? "Tell me about one of those user interviews you did on the side. What did you find that the product team didn't already know?"
              : "Walk me through the last time you were called in for that. What was broken, and what did you actually do?"}
            <span className="briefing-cursor" aria-hidden="true" />
          </div>
        </div>
      </div>

      <div className="briefing-instructions">
        <div className="briefing-instruction">
          <div className="bi-badge">○</div>
          <div>
            <strong>No time limit</strong>
            <p>Take as long as you need. Thoughtful answers produce better results.</p>
          </div>
        </div>
        <div className="briefing-instruction">
          <div className="bi-badge">◇</div>
          <div>
            <strong>Write naturally</strong>
            <p>Use your own words. You don't need to sound formal — just be specific and honest.</p>
          </div>
        </div>
        <div className="briefing-instruction">
          <div className="bi-badge">→</div>
          <div>
            <strong>One question at a time</strong>
            <p>Each answer unlocks the next question. You can't skip ahead or go back.</p>
          </div>
        </div>
        <div className="briefing-instruction">
          <div className="bi-badge">✦</div>
          <div>
            <strong>Profile at the end</strong>
            <p>When you finish, your career profile and portfolio assets appear automatically.</p>
          </div>
        </div>
      </div>

      <div className="briefing-cta-group">
        <button className="primary-action" onClick={onBegin} data-testid="begin-interview-button">
          Begin Interview <ArrowRight size={18} />
        </button>
        <p className="briefing-time-note">{b.timeNote}</p>
      </div>
    </div>
  );
}
